import { cn } from "@healthos/ui";
import { Check, type Icon as PhosphorIcon } from "@phosphor-icons/react";

export type ChoiceOption =
  | string
  | {
      value: string;
      label: string;
      Icon?: PhosphorIcon;
      caption?: string;
    };

type ChoiceFieldProps = {
  label: string;
  value: string;
  options: readonly ChoiceOption[];
  onChange: (value: string) => void;
  columns?: 2 | 3 | 5;
  compact?: boolean;
};

export function ChoiceField({
  label,
  value,
  options,
  onChange,
  columns = 3,
  compact = false,
}: ChoiceFieldProps) {
  const gridColumns =
    columns === 2
      ? "grid-cols-2"
      : columns === 5
        ? "grid-cols-2 sm:grid-cols-5"
        : "grid-cols-2 sm:grid-cols-3";

  return (
    <fieldset>
      <legend className="mb-3 font-sans text-xs font-medium text-ink-tertiary">{label}</legend>
      <div className={cn("grid", compact ? "gap-2" : "gap-2.5", gridColumns)}>
        {options.map((option) => {
          const optionValue = typeof option === "string" ? option : option.value;
          const optionLabel = typeof option === "string" ? capitalize(option) : option.label;
          const Icon = typeof option === "string" ? undefined : option.Icon;
          const caption = typeof option === "string" ? undefined : option.caption;
          const selected = value === optionValue;

          return (
            <button
              key={optionValue}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(optionValue)}
              className={cn(
                "group relative flex items-center gap-3 rounded-2xl border py-3 transition-[background-color,border-color,color,box-shadow,transform] duration-200 ease-out active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 motion-reduce:transform-none",
                compact
                  ? "min-h-[62px] justify-center px-3 text-center"
                  : "min-h-[76px] px-4 text-left",
                selected
                  ? "border-navy bg-navy text-ink-on-brand shadow-[0_16px_30px_-22px_color-mix(in_srgb,var(--color-scrim)_80%,transparent)]"
                  : "border-border-default bg-surface-elevated text-ink-primary hover:border-border-primary hover:bg-surface-secondary/45",
              )}
            >
              {Icon ? (
                <span
                  className={cn(
                    "grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors",
                    selected
                      ? "bg-surface-elevated/14 text-ink-on-media"
                      : "bg-surface-secondary text-ink-brand",
                  )}
                >
                  <Icon size={19} weight={selected ? "fill" : "duotone"} aria-hidden="true" />
                </span>
              ) : null}
              <span className={cn("min-w-0", compact ? "flex-none" : "flex-1")}>
                <span className="block text-base font-medium leading-snug">{optionLabel}</span>
                {caption ? (
                  <span
                    className={cn(
                      "mt-1 block font-sans font-medium text-xs",
                      selected ? "text-teal-200" : "text-ink-tertiary",
                    )}
                  >
                    {caption}
                  </span>
                ) : null}
              </span>
              {selected && !compact ? <Check size={16} weight="bold" aria-hidden="true" /> : null}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

export function RangeField({
  label,
  value,
  onChange,
  min = "1",
  max = "5",
  step = "1",
  minLabel,
  maxLabel,
  valueFormatter = (next) => next,
  editorial = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  step?: string;
  minLabel?: string;
  maxLabel?: string;
  valueFormatter?: (value: string) => string;
  editorial?: boolean;
}) {
  const lower = Number(min);
  const upper = Number(max);
  const current = Number(value);
  const progress =
    Number.isFinite(current) && upper > lower
      ? Math.min(100, Math.max(0, ((current - lower) / (upper - lower)) * 100))
      : 0;

  return (
    <label
      className={cn(
        "block transition-colors has-focus-visible:border-teal-500 has-focus-visible:bg-surface-secondary/35",
        editorial ? "" : "rounded-xl border border-border-default bg-surface-elevated px-4 py-3.5",
      )}
    >
      <span className="flex items-center justify-between gap-3">
        <span className="font-sans text-xs font-medium text-ink-tertiary">{label}</span>
        <output
          className={cn(
            "tabular-nums",
            editorial
              ? "font-sans text-5xl font-medium leading-none tracking-[-0.035em] text-ink-primary"
              : "rounded-full bg-surface-secondary px-2.5 py-1 text-sm font-semibold text-teal-800",
          )}
        >
          {valueFormatter(value)}
        </output>
      </span>
      <input
        type="range"
        aria-label={label}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={{
          background: `linear-gradient(to right, var(--color-ink-brand) 0%, var(--color-ink-brand) ${progress}%, var(--color-surface-track) ${progress}%, var(--color-surface-track) 100%)`,
        }}
        className="mt-5 h-2.5 w-full cursor-pointer appearance-none rounded-full bg-surface-track outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-teal-200 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:cursor-grab [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-surface-elevated [&::-moz-range-thumb]:bg-teal-700 [&::-moz-range-thumb]:shadow-[0_2px_8px_color-mix(in_srgb,var(--color-scrim)_24%,transparent)] [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-surface-elevated [&::-webkit-slider-thumb]:bg-teal-700 [&::-webkit-slider-thumb]:shadow-[0_2px_8px_color-mix(in_srgb,var(--color-scrim)_24%,transparent)]"
      />
      {minLabel || maxLabel ? (
        <span className="mt-2 flex justify-between text-xs text-ink-tertiary">
          <span>{minLabel ?? min}</span>
          <span>{maxLabel ?? max}</span>
        </span>
      ) : null}
    </label>
  );
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <FieldLabel>{label}</FieldLabel>
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={inputClassName}
      />
    </label>
  );
}

export function NumberField({
  label,
  value,
  onChange,
  suffix,
  min,
  max,
  step,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  suffix?: string;
  min?: string;
  max?: string;
  step?: string;
}) {
  return (
    <label className="block">
      <FieldLabel>{label}</FieldLabel>
      <span className="relative block">
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(event) => onChange(event.target.value)}
          className={cn(inputClassName, suffix ? "pr-16" : "")}
        />
        {suffix ? (
          <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-sm text-ink-tertiary">
            {suffix}
          </span>
        ) : null}
      </span>
    </label>
  );
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  optionLabel = capitalize,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly string[];
  optionLabel?: (value: string) => string;
}) {
  return (
    <label className="block">
      <FieldLabel>{label}</FieldLabel>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={inputClassName}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {optionLabel(option)}
          </option>
        ))}
      </select>
    </label>
  );
}

export function DateTimeField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block max-w-sm">
      <FieldLabel>When did this happen?</FieldLabel>
      <input
        type="datetime-local"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={inputClassName}
      />
    </label>
  );
}

export function TextAreaField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <label className="block">
      <FieldLabel>{`${label} (optional)`}</FieldLabel>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={3}
        placeholder={placeholder}
        className={cn(inputClassName, "min-h-[104px] resize-y leading-relaxed")}
      />
    </label>
  );
}

function FieldLabel({ children }: { children: string }) {
  return (
    <span className="mb-3 flex items-center gap-4 font-sans text-xs font-medium text-ink-tertiary">
      <span className="shrink-0">{children}</span>
      <span aria-hidden="true" className="h-px min-w-0 flex-1 bg-surface-track" />
    </span>
  );
}

const inputClassName =
  "w-full rounded-xl border border-border-default bg-surface-elevated px-4 py-3 text-sm text-ink-primary outline-none transition-[border-color,box-shadow,background-color] placeholder:text-ink-quaternary hover:border-border-strong/70 focus:border-teal-500 focus:bg-surface-elevated focus:ring-2 focus:ring-teal-100";

function capitalize(value: string): string {
  return `${value.slice(0, 1).toUpperCase()}${value.slice(1).replaceAll("_", " ")}`;
}
